import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/consulting", label: "Consulting" },
  { to: "/peer-support", label: "Peer Support" },
  { to: "/packages", label: "Packages" },
  { to: "/resources", label: "Resources" },
  { to: "/faq", label: "FAQ" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#F9F7F3]/85 border-b border-[#1B4332]/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-full bg-brand-green flex items-center justify-center text-brand-gold font-serif text-xl">
            K
          </div>
          <div className="leading-tight">
            <div className="font-serif text-xl text-brand-green tracking-tight">KBX Pathways</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-sage">To Wellness</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-brand-green" : "text-slate-600 hover:text-brand-green"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-book-cta"
            className="inline-flex items-center gap-2 bg-brand-green text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-brand-navy transition-colors"
          >
            Book a Consultation
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          className="lg:hidden text-brand-green"
          aria-label="menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#1B4332]/10 bg-[#F9F7F3]" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="text-base text-slate-700 hover:text-brand-green py-2 border-b border-slate-100"
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center gap-2 bg-brand-green text-white rounded-full px-5 py-3 text-sm font-medium justify-center"
            >
              Book a Consultation <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="bg-brand-navy text-slate-200 mt-24">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-gold flex items-center justify-center text-brand-navy font-serif text-xl">
            K
          </div>
          <div>
            <div className="font-serif text-xl text-white">KBX Pathways To Wellness</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-sage">Raleigh, North Carolina</div>
          </div>
        </div>
        <p className="mt-6 text-slate-300 max-w-md font-light leading-relaxed">
          Building pathways to healing, structure, and sustainable wellness — for peer support
          agencies, behavioral health entrepreneurs, and the communities they serve.
        </p>
      </div>
      <div className="md:col-span-3">
        <h4 className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-4">Explore</h4>
        <ul className="space-y-2 text-sm">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link to={n.to} className="text-slate-300 hover:text-white">{n.label}</Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-4">
        <h4 className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-4">Contact</h4>
        <ul className="space-y-2 text-sm text-slate-300">
          <li>info@kbxpathwaystowellness.com</li>
          <li>Raleigh, North Carolina</li>
          <li><Link to="/contact" className="underline underline-offset-4 hover:text-white">Book a Consultation</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 text-xs text-slate-400 leading-relaxed">
        <p className="max-w-4xl">
          KBX Pathways To Wellness provides consulting, educational, and preparation support.
          We do not guarantee credentialing approval, Medicaid enrollment, payer acceptance,
          funding approval, licensing outcomes, or contract awards. Clients are responsible for
          final submissions, legal compliance, and professional/licensing requirements. When
          needed, clients should consult with licensed legal, tax, insurance, or healthcare
          compliance professionals.
        </p>
        <p className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <span>© {new Date().getFullYear()} KBX Pathways To Wellness. All rights reserved.</span>
          <Link to="/admin/login" className="text-slate-500 hover:text-slate-300">Admin</Link>
        </p>
      </div>
    </div>
  </footer>
);

const SiteLayout = () => {
  const location = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);
  return (
    <div className="bg-brand-cream min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default SiteLayout;
